import { useMemo } from 'react';

import { mapIntegrationProviderToDTO } from '@/modules/common/mappers/integration.mapper';
import { IntegrationServices } from '@/modules/common/types/integrations';

import {
  TeamMemberChangedField,
  TeamMemberChangeLogEntry,
  TeamMemberChangeType,
} from '../types/team-member-changes';
import { TeamMemberIntegration } from '../types/team-member-integration';

interface UseResolvedIntegrationChangesParams {
  integrations?: TeamMemberIntegration[];
  changesItems?: TeamMemberChangeLogEntry[];
}

/**
 * Resolves raw integration IDs in change log entries to human-readable account names.
 *
 * Uses two sources to build the ID → name mapping:
 * 1. Current integrations (selectedValue) — the currently connected account
 * 2. Consecutive change entries — infers historical names by matching
 *    the 'to' ID of one change with the 'from' name of the next
 *
 * Current integration names take priority over history-derived names.
 */
export const useResolvedIntegrationChanges = ({
  integrations,
  changesItems,
}: UseResolvedIntegrationChangesParams): TeamMemberChangeLogEntry[] => {
  // Build a lookup map: { [providerDTO]: { [integrationId]: accountName } }
  const integrationIdToNameMap = useMemo(() => {
    const map: Record<string, Record<string, string>> = {};
    integrations?.forEach(integration => {
      if (integration.selectedValue?.id && integration.selectedValue?.name) {
        const providerDTO = mapIntegrationProviderToDTO(integration.type as IntegrationServices);
        if (!map[providerDTO]) map[providerDTO] = {};
        map[providerDTO][integration.selectedValue.id] = integration.selectedValue.name;
      }
    });
    return map;
  }, [integrations]);

  // Post-process integration change entries to replace raw IDs with account names
  return useMemo(() => {
    if (!changesItems?.length) return changesItems ?? [];

    // Build a supplementary map from consecutive integration change entries:
    // When change A sets 'to' to ID X, and the following change B for the same field
    // has 'from' = "Name of X", we can infer that account ID X has that name.
    // This covers historical connections that are no longer the current selectedValue.
    const changeBasedNameMap: Record<string, Record<string, string>> = {};

    const integrationChanges = changesItems
      .filter(entry => entry.type === TeamMemberChangeType.Integration)
      .slice()
      .sort((a, b) => (a.startDate < b.startDate ? -1 : a.startDate > b.startDate ? 1 : 0));

    const prevToByField: Record<string, string | null> = {};

    for (const entry of integrationChanges) {
      for (const field of entry.changedFields) {
        // Handle both grouped fields (subfieldChanges) and individual fields
        const fieldsToProcess =
          field.isGrouped && field.subfieldChanges ? field.subfieldChanges : [field];

        for (const f of fieldsToProcess) {
          const prevId = prevToByField[f.field];
          // The 'from' of this change is the name of the account that was set in the previous change
          if (prevId && f.from) {
            if (!changeBasedNameMap[f.field]) changeBasedNameMap[f.field] = {};
            changeBasedNameMap[f.field][prevId] = f.from;
          }
          prevToByField[f.field] = f.to;
        }
      }
    }

    // Merge maps: current selectedValue-based names take priority over history-derived names
    const mergedNameMap: Record<string, Record<string, string>> = {};
    const allFieldKeys = new Set([
      ...Object.keys(integrationIdToNameMap),
      ...Object.keys(changeBasedNameMap),
    ]);
    for (const key of allFieldKeys) {
      mergedNameMap[key] = {
        ...(changeBasedNameMap[key] || {}),
        ...(integrationIdToNameMap[key] || {}),
      };
    }

    const resolveField = (field: TeamMemberChangedField): TeamMemberChangedField => {
      const nameMap = mergedNameMap[field.field];
      const resolvedTo = nameMap && field.to ? (nameMap[field.to] ?? field.to) : field.to;
      return {
        ...field,
        to: resolvedTo,
        subfieldChanges: field.subfieldChanges?.map(resolveField) ?? null,
      };
    };

    return changesItems.map(entry => {
      if (entry.type !== TeamMemberChangeType.Integration) return entry;
      return { ...entry, changedFields: entry.changedFields.map(resolveField) };
    });
  }, [changesItems, integrationIdToNameMap]);
};
